/**
 * TRACK 3 — Performance instrumentation.
 *
 * Wraps the browser's Performance API into a tiny per-session telemetry
 * buffer the operator can dump from the admin diagnostics surface.
 *
 * No server traffic — instrumentation is read locally to answer:
 *   - Are sheet opens under 2s?
 *   - Are zooms under 100ms?
 *   - Is pan instant?
 *
 * Buffer is bounded to the most recent 200 samples per metric so memory
 * stays cheap on a long-running iPad session.
 */

const BUF_MAX = 200;
const _samples = {
  sheet_open: [],
  tile_load: [],
  first_render: [],
  zoom: [],
  pan: [],
  sheet_switch: [],
};

function _push(metric, ms) {
  const buf = _samples[metric];
  if (!buf) return;
  buf.push(ms);
  if (buf.length > BUF_MAX) buf.splice(0, buf.length - BUF_MAX);
}

export function markStart(label) {
  try { performance.mark(`${label}:start`); } catch { /* swallow */ }
}

export function markEnd(label, metric) {
  try {
    performance.mark(`${label}:end`);
    performance.measure(label, `${label}:start`, `${label}:end`);
    const entries = performance.getEntriesByName(label);
    const last = entries[entries.length - 1];
    if (last && metric) _push(metric, last.duration);
    // Clean up after every measure so the buffer doesn't bloat.
    performance.clearMarks(`${label}:start`);
    performance.clearMarks(`${label}:end`);
    performance.clearMeasures(label);
    return last ? last.duration : null;
  } catch {
    return null;
  }
}

function _stats(arr) {
  if (!arr.length) return { n: 0, avg: 0, p50: 0, p95: 0, worst: 0 };
  const sorted = [...arr].sort((a, b) => a - b);
  const n = sorted.length;
  const sum = sorted.reduce((a, b) => a + b, 0);
  const pick = (p) => sorted[Math.min(n - 1, Math.floor((p / 100) * n))];
  return {
    n,
    avg: Math.round(sum / n),
    p50: Math.round(pick(50)),
    p95: Math.round(pick(95)),
    worst: Math.round(sorted[n - 1]),
  };
}

export function getPerfReport() {
  // TRACK 3 targets (from directive):
  //   sheet_open < 2000ms
  //   zoom < 100ms
  //   pan = "instant" (we use <16ms p95 = 60fps as the target)
  const targets = {
    sheet_open: 2000,
    zoom: 100,
    pan: 16,
    sheet_switch: 1500,
    first_render: 1500,
    tile_load: 800,
  };
  const report = {};
  for (const [metric, target] of Object.entries(targets)) {
    const s = _stats(_samples[metric] || []);
    report[metric] = { ...s, target_ms: target, pass: s.n > 0 && s.p95 <= target };
  }
  return report;
}

export function clearPerf() {
  for (const k of Object.keys(_samples)) _samples[k] = [];
}

// Internal hook for tests
export const __perfInternals = { _samples };
